import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { AgencyModel, UserModel } from "@/database";
import { parseFilterFromUrl } from "@/utils/mediautils/CommonFunctions";
import { formatEntityName } from "@/utils/utilFunctions/entityName";

interface UseMediaListAutoSelectProps {
  filters: Record<string, any>;
  getListAgencies: (params: {
    name?: string;
    setInRedux: boolean;
  }) => Promise<any>;
  getListUser: (params: {
    name: string;
    setInRedux: boolean;
    team?: string[];
  }) => Promise<any>;
  singleCurrentUser?: any;
}

interface AutoSelectOptions {
  label: string;
  value: string;
}

interface UseMediaListAutoSelectReturn {
  autoselectAgency: AgencyModel[];
  autoselectedUploader: UserModel[];
  autoselectedModifier: UserModel[];
  onAutoSelectInputChange: (params: { name: string; keyword: string }) => void;
  autoSelectOptionsMap: {
    agency: AutoSelectOptions[];
    agencyQuick: AutoSelectOptions[];
    ctby: AutoSelectOptions[];
    mtbyuser: AutoSelectOptions[];
  };
}
let cachedAgencyInitialOptions: AgencyModel[] | null = null;

/**
 * Custom hook to manage auto-select behavior for filters in MediaList
 * Handles agency, uploader (ctby), and modifier (mtbyuser) auto-select functionality
 * Extracted to improve code organization and reduce component complexity
 */
export const useMediaListAutoSelect = ({
  filters,
  getListAgencies,
  getListUser,
  singleCurrentUser,
}: UseMediaListAutoSelectProps): UseMediaListAutoSelectReturn => {
  const [searchParams] = useSearchParams();

  const [autoselectAgency, setAutoselectAgency] = useState<AgencyModel[]>([]);
  const [agencyInitialOptions, setAgencyInitialOptions] = useState<
    AgencyModel[]
  >(cachedAgencyInitialOptions ?? []);
  const [autoselectedUploader, setAutoselectedUploader] = useState<UserModel[]>(
    []
  );
  const [autoselectedModifier, setAutoselectedModifier] = useState<UserModel[]>(
    []
  );

  useEffect(() => {
    if (cachedAgencyInitialOptions) return;
    getListAgencies({ setInRedux: false }).then((res) => {
      const agencies = (res || []) as unknown as AgencyModel[];
      if (agencies.length > 0) {
        cachedAgencyInitialOptions = agencies;
        setAgencyInitialOptions(agencies);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parse auto-select values from URL on mount and when search params change
  useEffect(() => {
    const currentUrl = window.location.href;

    const parsedAgencies = parseFilterFromUrl(
      currentUrl,
      "agency",
      "agencydisplayValue"
    );
    if (parsedAgencies.length > 0) {
      setAutoselectAgency([...(parsedAgencies as AgencyModel[])]);
    }

    const parsedCtbyUsers = parseFilterFromUrl(
      currentUrl,
      "ctby",
      "ctbydisplayValue"
    );
    if (parsedCtbyUsers.length > 0) {
      setAutoselectedUploader([...(parsedCtbyUsers as UserModel[])]);
    }

    const parsedMtbyUsers = parseFilterFromUrl(
      currentUrl,
      "mtbyuser",
      "mtbyuserdisplayValue"
    );
    if (parsedMtbyUsers.length > 0) {
      setAutoselectedModifier([...(parsedMtbyUsers as UserModel[])]);
    }
  }, [searchParams.toString()]); // Use toString() to get a stable string representation

  // Clear auto-select values when corresponding filters are removed
  useEffect(() => {
    if (!Object.keys(filters).includes("mtbyuser")) {
      setAutoselectedModifier([]);
    }
    if (!Object.keys(filters).includes("ctby")) {
      setAutoselectedUploader([]);
    }
    if (!Object.keys(filters).includes("agency")) {
      setAutoselectAgency([]);
    }
  }, [filters]);

  /**
   * Handles input change for auto-select fields
   * Fetches suggestions when keyword length >= 3, clears when < 3
   */
  const onAutoSelectInputChange = useCallback(
    (params: { name: string; keyword: string }) => {
      const keyword = (params.keyword || "").trim();
      if (keyword.length >= 3) {
        if (params.name === "agency") {
          getListAgencies({ name: keyword, setInRedux: false }).then((res) => {
            const agencies = (res || []) as unknown as AgencyModel[];
            setAutoselectAgency(agencies);
          });
        } else if (params.name === "ctby" || params.name === "mtbyuser") {
          const userSearchParams: {
            name: string;
            setInRedux: boolean;
          } = { name: keyword, setInRedux: false };
          getListUser(userSearchParams).then((res) => {
            if (params.name === "ctby") {
              setAutoselectedUploader(res || ([] as UserModel[]));
            } else if (params.name === "mtbyuser") {
              setAutoselectedModifier(res || ([] as UserModel[]));
            }
          });
        }
      } else {
        if (params.name === "agency") {
          setAutoselectAgency([]);
        } else if (params.name === "ctby" || params.name === "mtbyuser") {
          setAutoselectedUploader([]);
          setAutoselectedModifier([]);
        }
      }
    },
    [getListAgencies, getListUser, singleCurrentUser?.permissions]
  );

  /**
   * Memoized map of auto-select options for each filter type
   * Transforms the state arrays into the format expected by the Table component
   */
  const autoSelectOptionsMap = useMemo(
    () => ({
      agency: (autoselectAgency || [])?.map((item: AgencyModel) => ({
        label: formatEntityName(item),
        value: String(item?.id),
      })),
      agencyQuick: (agencyInitialOptions || []).map((item: AgencyModel) => ({
        label: formatEntityName(item),
        value: String(item?.id),
      })),
      ctby: (autoselectedUploader || [])?.map((item: UserModel) => ({
        label: item?.name + (item?.email ? " (" + item?.email + ")" : ""),
        value: String(item?.id),
      })),
      mtbyuser: (autoselectedModifier || [])?.map((item: UserModel) => ({
        label: item?.name,
        value: String(item?.id),
      })),
    }),
    [
      autoselectAgency,
      agencyInitialOptions,
      autoselectedUploader,
      autoselectedModifier,
    ]
  );

  return {
    autoselectAgency,
    autoselectedUploader,
    autoselectedModifier,
    onAutoSelectInputChange,
    autoSelectOptionsMap,
  };
};
